import React, {useEffect, useState} from "react";
import {Typography, Grid, Link, Rating, Box} from "@mui/material";
import {Clip } from '../../../types/clipTypes'
import {capitilize} from "../../../utils/stringUtils";
import {formatDuration} from "../../../utils/dateTimeutils";
import {formatJobExtraction} from "../../../utils/utils";
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import ReplayIcon from '@mui/icons-material/Replay';
import {useTheme} from "../../../contexts/theme";
import DeleteIcon from "@mui/icons-material/Delete";

interface ClipTableProps{
    clips: Clip[]
    onClipClick: (clip: Clip) => void;
    onClipDeleteClick: (clip: Clip) => void;
    mainClip: string
}

const ClipTable: React.FC<ClipTableProps> = ({mainClip, clips, onClipClick, onClipDeleteClick}) => {

    const theme = useTheme();
    const visibleColumns = ['delete', 'job_extraction_schedule', 'duration', 'job_settings', 'replay']

    function renderColName(colId: keyof Clip){
        let label = '-'
        if (colId === 'job_extraction_schedule'){
            label = 'Job'
        }else if (colId ===  'job_settings'){
            label = 'Settings';
        } else if (['isNew', 'replay', 'delete'].includes(colId)){
            label = ''
        }else{
            label = capitilize(colId)
        }
        return <TitleTypography>{label}</TitleTypography>
    }

    function renderColValue(colId: keyof Clip | 'delete', clip: Clip){
        if (colId === 'job_extraction_schedule') {
            return (<LinkTypography clip={clip}>{formatJobExtraction(clip[colId])}</LinkTypography>)
        }else if(colId === 'duration'){
                return (<DescriptionTypography>{formatDuration(clip[colId])}</DescriptionTypography>)
        }else if(colId === 'socials'){
            return clip[colId].map(social => <Box sx={{display:'flex', justifyContent: 'center'}}><AccessibilityIcon sx={{fontSize:theme.fontSizes.description}}></AccessibilityIcon></Box>)
        }else if(colId === 'replay') {
            return clip[colId] ? <Box sx={{display:'flex', justifyContent: 'center'}}><ReplayIcon sx={{fontSize:theme.fontSizes.description}}></ReplayIcon></Box> : <></>;
        }else if(colId === 'isNew'){
            return clip[colId] ? <Box sx={{display:'flex', justifyContent: 'center'}}><AccessibilityIcon sx={{fontSize:theme.fontSizes.description}}></AccessibilityIcon></Box> : <></>;
        }else if(colId === 'delete') {
            return <Box sx={{display:'flex', justifyContent: 'center'}}><DeleteIcon onClick={() => onClipDeleteClick(clip)} sx={{fontSize:theme.fontSizes.description}}/></Box>;
        }
        return <DescriptionTypography>{clip[colId]}</DescriptionTypography>;
    }

    function getColSpan(colId: keyof Clip): number{
        if (['replay', 'isNew', 'delete'].includes(colId)){
            return 1
        }else if (colId === 'job_extraction_schedule'){
            return 3
        }
        return 2;
    }

    const TitleTypography: React.FC<React.PropsWithChildren> = ({children}) => {
        return <Typography align="center" fontSize={theme.fontSizes.description} fontWeight={theme.fontWeights.title}>{children}</Typography>
    }

    const DescriptionTypography: React.FC<React.PropsWithChildren> = ({children}) => {
        return <Typography align="center" fontSize={theme.fontSizes.description} >{children}</Typography>
    }

    interface LinkTypographyProps{
        children?: React.ReactNode
        clip: Clip
    }

    const LinkTypography: React.FC<LinkTypographyProps> = ({children, clip}) => {
        return <Typography
            onClick={() => onClipClick(clip)}
            align="center"
            fontSize={theme.fontSizes.description}
            sx={{
                cursor: 'pointer',
                color: clip.id !== mainClip ? theme.palette.orange : theme.palette.lightGrey,
                textDecoration: clip.id !== mainClip ? 'underline' : '',
                textAlign: 'center'
            }}
        >
            {children}
        </Typography>
    }

    return (
        <Box sx={{width: '100%', marginBottom: theme.spacing.sm, marginTop: theme.spacing.sm}}>
            {clips.map((clip, index) => (
                <Box key={'ClipTable' + index}>
                    <Grid container justifyContent="space-between">
                        {visibleColumns.map((column) => (
                            <Grid item key={'ClipTableColumnName' + column} xs={getColSpan(column as keyof Clip)} sx={{width: '100%'}}>
                                    {renderColName(column as keyof Clip)}
                            </Grid>
                        ))}
                    </Grid>
                    <Grid container justifyContent="space-between">
                        {visibleColumns.map((column) => (
                            <Grid key={'ClipTableColumnValue' + column} item xs={getColSpan(column as keyof Clip)} sx={{width: '100%'}}>
                                {renderColValue(column as keyof Clip, clip)}
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
        </Box>
    );
};

export default React.memo(ClipTable);